import { Box, Divider, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import MDButton from "components/MDButton";
import Modal from "modules/Modal/Modal";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { AllocationPreview, EditableLine, SyncableJob } from "./quickbooksSyncUtils";

type Props = {
  allocationPreview: AllocationPreview;
  billToLines: string[];
  customerEmail: string;
  customerName: string;
  customerPhone: string;
  invoiceNumber: string;
  jobName: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  selectedJobs: SyncableJob[];
  shipToLines: string[];
  syncEstimate: boolean;
  syncInvoice: boolean;
  taxReferenceLabel: string;
  visibleLines: EditableLine[];
};

export default function QuickbooksSyncPreviewModal({
  allocationPreview,
  billToLines,
  customerEmail,
  customerName,
  customerPhone,
  invoiceNumber,
  jobName,
  loading,
  onClose,
  onConfirm,
  open,
  selectedJobs,
  shipToLines,
  syncEstimate,
  syncInvoice,
  taxReferenceLabel,
  visibleLines,
}: Props) {
  const documentLabel = syncInvoice && syncEstimate ? "QuickBooks Sync" : syncInvoice ? "Invoice" : "Estimate";
  const confirmLabel = syncInvoice ? "Create Invoice" : syncEstimate ? "Create Estimate" : "Confirm Sync";

  return (
    <Modal
      open={open}
      onClose={onClose}
      styles={{ width: "min(980px, 94vw)", height: "min(860px, 90vh)", maxWidth: "980px" }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ pr: 5, mb: 2 }}>
          <Typography variant="overline" color="primary.main" fontWeight={700} lineHeight={1}>
            Preview Sync
          </Typography>
          <Typography variant="h5">QuickBooks {documentLabel} Preview</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            This is the information that will be submitted to QuickBooks.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ overflow: "hidden", mb: 2 }}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} mb={3}>
              <Box>
                <Typography variant="h5">{documentLabel}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Document #{invoiceNumber || "Assigned by QuickBooks"}
                </Typography>
              </Box>
              <Box textAlign={{ sm: "right" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Amount to sync
                </Typography>
                <Typography variant="h5" color="primary.main">
                  {formatCentsToUSD(allocationPreview.billedTotal)}
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={3} mb={3}>
              <Box flex={1}>
                <Typography variant="overline" color="text.secondary" lineHeight={1.5}>
                  Bill To
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {customerName || "Customer"}
                </Typography>
                {customerEmail && <Typography variant="body2">{customerEmail}</Typography>}
                {customerPhone && <Typography variant="body2">{customerPhone}</Typography>}
                {billToLines.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Box>
              <Box flex={1}>
                <Typography variant="overline" color="text.secondary" lineHeight={1.5}>
                  Ship To
                </Typography>
                {jobName && (
                  <Typography variant="body2" fontWeight={700}>
                    {jobName}
                  </Typography>
                )}
                {shipToLines.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Box>
            </Stack>

            <Divider />

            <Box py={2}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                Included jobs
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {selectedJobs.map((job) => (
                  <Typography key={job.id} variant="body2">
                    {job.externalId} - {job.name}
                  </Typography>
                ))}
              </Stack>
            </Box>

            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell sx={{ width: "25%" }}>Item</TableCell>
                  <TableCell>Item Description</TableCell>
                  <TableCell align="right" sx={{ width: "20%" }}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleLines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell sx={{ verticalAlign: "top" }}>{line.itemName || "Untitled line"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap", verticalAlign: "top" }}>
                      {line.itemDescription || "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: "top" }}>
                      {formatCentsToUSD(allocationPreview.byLine.get(line.id) ?? 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Stack spacing={0.75} mt={2} alignItems="flex-end">
              <Box sx={{ display: "flex", justifyContent: "space-between", width: 260, gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">{formatCentsToUSD(allocationPreview.billedTotal)}</Typography>
              </Box>
              <Box sx={{ width: 260 }}>
                <Typography variant="caption" color="text.secondary">
                  Tax: {taxReferenceLabel || "No tax reference selected"}. Tax is applied in QuickBooks.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: 260, gap: 2 }}>
                <Typography variant="body1" fontWeight={700}>
                  Amount to sync
                </Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">
                  {formatCentsToUSD(allocationPreview.billedTotal)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <MDButton color="light" disabled={loading} onClick={onClose}>
            Back to Edit
          </MDButton>
          <MDButton color="success" disabled={loading} onClick={onConfirm}>
            {confirmLabel}
          </MDButton>
        </Stack>
      </Box>
    </Modal>
  );
}
